import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MapPin } from 'lucide-react';
import { company } from '../mock';

const Crumb = ({ crumb, title, dark }) => (
  <div className="flex items-center gap-2 text-sm font-semibold">
    <Link to="/" className={`${dark ? 'text-slate-500' : 'text-white/80'} hover:text-brand-orange transition-colors`}>Home</Link>
    <ChevronRight className={`w-4 h-4 ${dark ? 'text-slate-400' : 'text-white/60'}`} />
    <span className="text-brand-orange">{crumb || title}</span>
  </div>
);

const PageBanner = ({ title, subtitle, crumb, image, images, variant = 'overlay' }) => {
  /* Variant: SPLIT — teal panel left, faded image right (About) */
  if (variant === 'split') {
    return (
      <section className="relative bg-brand-blue overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="px-5 lg:px-8 py-16 lg:py-24 relative z-10">
            <Crumb crumb={crumb} title={title} />
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mt-5 mb-4">{title}</h1>
            {subtitle && <p className="font-body text-white/85 max-w-md">{subtitle}</p>}
          </div>
          <div className="relative min-h-[240px] lg:min-h-full">
            <img
              src={image}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                maskImage: 'linear-gradient(to right, transparent 0%, black 32%, black 78%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 32%, black 78%, transparent 100%)',
              }}
            />
          </div>
        </div>
      </section>
    );
  }

  /* Variant: LIGHT — light bg, heading left, floating image card right (Careers) */
  if (variant === 'light') {
    return (
      <section className="relative bg-brand-blue-light overflow-hidden">
        <div className="blob w-72 h-72 bg-brand-mint/40 -top-20 right-1/3" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <Crumb crumb={crumb} title={title} dark />
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mt-5 mb-4">{title}</h1>
            {subtitle && <p className="font-body text-slate-600 max-w-md">{subtitle}</p>}
          </div>
          <div className="relative hidden lg:block">
            <img src={image} alt={title} className="rounded-3xl object-cover w-full h-64 shadow-xl" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-brand-orange/15 rounded-2xl -z-0" />
          </div>
        </div>
      </section>
    );
  }

  /* Variant: SERVICES — solid teal text panel (max legibility) + image collage right */
  if (variant === 'services') {
    const set = images && images.length ? images : [image, image, image];
    return (
      <section className="relative bg-brand-blue overflow-hidden">
        <div className="absolute inset-0 text-white/5 dots-pattern" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Crumb crumb={crumb} title={title} />
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl xl:text-6xl text-white mt-5 leading-[1.05] drop-shadow-sm">
              {title}
            </h1>
            <span className="block w-20 h-1.5 rounded-full bg-brand-mint mt-5 mb-6" />
            {subtitle && <p className="font-body text-lg text-white/90 max-w-lg">{subtitle}</p>}
          </div>

          <div className="hidden lg:grid grid-cols-2 grid-rows-2 gap-3 h-72">
            <img src={set[0]} alt="" className="row-span-2 w-full h-full object-cover rounded-2xl shadow-lg" />
            <img src={set[1]} alt="" className="w-full h-full object-cover rounded-2xl shadow-lg" />
            <img src={set[2]} alt="" className="w-full h-full object-cover rounded-2xl shadow-lg" />
          </div>
        </div>
      </section>
    );
  }

  /* Variant: CONTACT — heading + contact chips left, crisp image card right */
  if (variant === 'contact') {
    const chips = [
      { icon: Phone, text: company.primaryPhone },
      { icon: Mail, text: company.email },
      { icon: MapPin, text: 'Dundalk, Co. Louth' },
    ];
    return (
      <section className="relative bg-gradient-to-br from-brand-blue to-brand-blue-dark overflow-hidden">
        <div className="blob w-80 h-80 bg-brand-mint/20 -bottom-28 -left-16" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Crumb crumb={crumb} title={title} />
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mt-5 mb-4">{title}</h1>
            {subtitle && <p className="font-body text-white/85 max-w-md mb-8">{subtitle}</p>}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              {chips.map((c) => (
                <div key={c.text} className="inline-flex items-center gap-2.5 bg-white/10 border border-white/15 rounded-full pl-3 pr-5 py-2">
                  <span className="w-8 h-8 rounded-full bg-brand-mint flex items-center justify-center shrink-0">
                    <c.icon className="w-4 h-4 text-brand-blue-dark" />
                  </span>
                  <span className="font-body text-sm text-white">{c.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block">
            <img src={image} alt={title} className="rounded-3xl object-cover w-full h-72 shadow-2xl" />
            <div className="absolute -top-5 -right-4 bg-brand-mint text-brand-blue-dark rounded-2xl shadow-xl px-5 py-3 font-heading font-bold text-sm">
              Free first consultation
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* Variant (default): OVERLAY — full-width image with teal gradient, centered */
  return (
    <section className="relative overflow-hidden">
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-brand-blue-dark/92 via-brand-blue/80 to-brand-blue-dark/85" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 text-center">
        <h1 className="font-heading font-bold text-4xl md:text-5xl xl:text-6xl text-white mb-4">{title}</h1>
        {subtitle && <p className="font-body text-white/85 max-w-2xl mx-auto mb-6">{subtitle}</p>}
        <div className="flex justify-center"><Crumb crumb={crumb} title={title} /></div>
      </div>
    </section>
  );
};

export default PageBanner;
